import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../axios';

function toId(v) {
  if (!v) return null;
  const s = typeof v === 'string' ? v : ((v._id || v.id)?.toString() ?? v.toString());
  return /^[a-f\d]{24}$/i.test(s) ? s : null;
}

const biz = (getState) => {
  const u = getState().auth.user;
  if (!u) return null;
  return (
    toId(u.businessId) ||
    toId(u.activeBusiness?.id) ||
    toId(u.business?.id) ||
    toId(u.businesses?.[0]?.id) ||
    toId(u.businesses?.[0]?.business) ||
    null
  );
};

const NO_BIZ_ERR = 'No workspace found — please sign out and sign in again.';

// The gateway returns Mongo docs: `_id`, and `key` either masked ("abcd...wxyz")
// or, only on create/rotate, in plain text. Never keep the plain key in the list.
function normalizeKey(k) {
  if (!k) return k;
  const { key = '', _id, ...rest } = k;
  const maskedKey = key.includes('...')
    ? key.replace('...', '•••••')
    : key.length > 8 ? `${key.slice(0, 4)}•••••${key.slice(-4)}` : key;
  return { ...rest, id: (rest.id || _id)?.toString(), maskedKey };
}

export const fetchApiKeys = createAsyncThunk('apiKeys/fetch', async (_, { getState, rejectWithValue }) => {
  try {
    const businessId = biz(getState);
    if (!businessId) return rejectWithValue(NO_BIZ_ERR);
    const { data } = await api.get(`/api-key/${businessId}/api-keys`);
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to load API keys');
  }
});

export const createApiKey = createAsyncThunk('apiKeys/create', async (payload, { getState, rejectWithValue }) => {
  try {
    const businessId = biz(getState);
    if (!businessId) return rejectWithValue(NO_BIZ_ERR);
    const { data } = await api.post(`/api-key/${businessId}/api-keys`, payload);
    return data;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to create API key');
  }
});

export const deleteApiKey = createAsyncThunk('apiKeys/delete', async (apiKeyId, { getState, rejectWithValue }) => {
  try {
    const businessId = biz(getState);
    if (!businessId) return rejectWithValue(NO_BIZ_ERR);
    await api.delete(`/api-key/${businessId}/api-keys/${apiKeyId}`);
    return apiKeyId;
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to delete API key');
  }
});

export const rotateApiKey = createAsyncThunk('apiKeys/rotate', async (apiKeyId, { getState, rejectWithValue }) => {
  try {
    const businessId = biz(getState);
    if (!businessId) return rejectWithValue(NO_BIZ_ERR);
    const { data } = await api.post(`/api-key/${businessId}/api-keys/${apiKeyId}/rotate`);
    return { data, oldId: apiKeyId };
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Failed to rotate API key');
  }
});

const apiKeySlice = createSlice({
  name: 'apiKeys',
  initialState: { keys: [], loading: false, error: null, newKey: null, lastFetched: null },
  reducers: {
    clearNewKey: (state) => { state.newKey = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchApiKeys.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchApiKeys.fulfilled, (state, { payload }) => {
        state.loading = false;
        const list = Array.isArray(payload) ? payload : (payload.apiKeys || payload.results || []);
        state.keys = list.map(normalizeKey);
        state.lastFetched = Date.now();
      })
      .addCase(fetchApiKeys.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(createApiKey.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createApiKey.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.newKey = payload.key || payload.plainTextKey;
        state.keys.unshift(normalizeKey(payload.apiKey || payload));
      })
      .addCase(createApiKey.rejected, (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(deleteApiKey.fulfilled, (state, { payload }) => {
        state.keys = state.keys.filter(k => k.id !== payload);
      })

      // Rotation issues a new key and deactivates the old one
      .addCase(rotateApiKey.fulfilled, (state, { payload: { data, oldId } }) => {
        state.newKey = data.key || data.plainTextKey;
        state.keys = state.keys.map(k => (k.id === oldId ? { ...k, isActive: false } : k));
        state.keys.unshift(normalizeKey(data.apiKey || data));
      })
      .addCase(rotateApiKey.rejected, (state, { payload }) => { state.error = payload; });
  },
});

export const { clearNewKey, clearError } = apiKeySlice.actions;
export default apiKeySlice.reducer;
